import { z } from 'zod';

import { ACTION_TYPES_WITH_EXECUTOR, type ActionType, type WorkflowAction } from './automation.js';
import { RESERVED_NAME_PREFIX, daySchema, listQuerySchema } from './common.js';
import { AgentFocus, agentFocusSchema } from './enums.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FAZ 4 — AJAN KİMLİĞİ SÖZLEŞMESİ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `agent.ts` ajanın SOHBET yüzü (konuşma, araç çağrısı, öneri kartı).
 * Bu dosya ajanın KİMLİK yüzü: adı, avatarı, odağı olan ve altında bir grup
 * `Workflow` taşıyan kalıcı nesne. İkisi ayrı dosyada çünkü ayrı iki modül —
 * sohbet bir oturum, ajan bir varlık.
 *
 * ---------------------------------------------------------------------------
 * Modelleme kararı (12 Ağu 2026)
 * ---------------------------------------------------------------------------
 * Üç bağımsız tasarım üretilip üç bağımsız jüriyle puanlandı; üçü de aynı
 * sırayı verdi. Kazanan: ajanın kendi tablosu + `Workflow.agentId` bağı.
 *
 * Elenen iki yol ve neden:
 *   - `Workflow.scope` JSON'una grup etiketi → `workflowScopeSchema` düz bir
 *     `z.object` ve Zod bilinmeyen anahtarları ATIYOR: ajan-kural bağı her
 *     kural düzenlemesinde sessizce silinirdi.
 *   - Tamamen türetme → ad ve avatar mevcut hiçbir kolondan türetilemiyor;
 *     "Bütçe Bekçisi adında bir ajan kur" gereksinimi karşılanamıyordu.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Odak alanı
// ═══════════════════════════════════════════════════════════════════════════

export const AGENT_FOCUSES: readonly AgentFocus[] = [
  AgentFocus.BUDGET,
  AgentFocus.CREATIVE,
  AgentFocus.BRAND_SAFETY,
  AgentFocus.KEYWORDS,
];

/**
 * Odak alanının ANLAMI: o odağa yakışan aksiyon tipleri.
 *
 * 🔴 Bu bir KISIT DEĞİL, bir eşleşme sözlüğü. Motor bunu okumuyor ve bir
 * kuralın ajana bağlanmasını ENGELLEMİYOR — yalnızca uyuşmazlıkta uyarı
 * üretiyor (`AGENT_FOCUS_ACTION_MISMATCH`). Sert kısıt olsaydı "bütçesi
 * aşınca duraklat ve bana yaz" gibi tamamen makul bir kural hiçbir ajana
 * bağlanamazdı.
 *
 * `pause`/`resume` iki odakta birden: bütçe bekçisi de marka bekçisi de
 * duraklatır, niyet farklı. Niyeti aksiyon tipinden okumaya çalışmak
 * yanlış olurdu — bu yüzden odağı KULLANICI seçiyor, sistem tahmin etmiyor.
 */
export const AGENT_FOCUS_ACTION_TYPES: Record<AgentFocus, readonly ActionType[]> = {
  BUDGET: ['budget_adjust', 'budget_redistribute', 'pause', 'resume'],
  CREATIVE: ['creative_improve'],
  BRAND_SAFETY: ['pause', 'notify', 'notify_external'],
  /**
   * Faz 5. `pause` BİLEREK YOK — kelime odaklı ajanın duraklattığı şey
   * kelimedir (`pause_keyword`), kampanya değil. `pause`u da listeye koymak
   * "kelime bekçisi" adlı bir ajanın bütün kampanyayı kapatmasını YAKIŞIR
   * gösterirdi ve uyarı çıkmadığı için kimse fark etmezdi.
   */
  KEYWORDS: ['add_negative_keyword', 'pause_keyword', 'adjust_bid'],
};

/**
 * 🔴 PARA SİNYALİ OLAN ODAKLAR — `ACTION_TYPES_WITH_EXECUTOR`in aynı fikri.
 *
 * Bir odağın tasarruf KPI'sı ancak o odağın aksiyonları `beforeState`e PARA
 * yazıyorsa hesaplanabilir. Bugün yalnız `budget_adjust` ve
 * `budget_redistribute` yazıyor (`{ dailyBudget }`); `pause`, `notify`,
 * `creative_improve` ve diğerleri `beforeState: null` ile doğuyor.
 *
 * Sonuç: `CREATIVE` ve `BRAND_SAFETY` odaklı bir ajanın para KPI'sı bugün
 * YAPISAL OLARAK her zaman 0. Ekranda `0` göstermek "bu ajan hiç para
 * kazandırmadı" demektir ve bu bir YALAN — doğrusu "bu odak için henüz para
 * sinyali yok". Arayüz bu listeye bakıp `0` yerine `—` basar.
 *
 * Bu, Faz 0'daki "tanımlı ama bağlı değil → sessizce düşüyor" hata sınıfının
 * ajan sürümü ve aynı ilaçla tedavi ediliyor: farkı tek bir yerde tut, ekrana
 * söylet.
 *
 * 🔴 `KEYWORDS` (Faz 5) BU LİSTEYE GİRMEDİ ve bu bir unutma değil. `adjust_bid`
 * `beforeState`e para YAZIYOR — ama `{ cpcBid }` yazıyor, `{ dailyBudget }`
 * değil. Tasarruf KPI'sının ham SQL'i günlük bütçe farkını topluyor; teklif
 * farkı ONUNLA AYNI ŞEY DEĞİL. Günlük bütçeyi 100'den 80'e çekmek günde 20
 * birim tasarruf demek, tıklama teklifini 2,00'den 1,60'a çekmek ise kaç
 * tıklama geleceğini bilmeden HİÇBİR ŞEY demek: trafik artarsa harcama artar.
 *
 * Listeye eklemek ekrana `0` bastırırdı ("bu ajan hiç para kazandırmadı" —
 * yalan). Doğru sayı ancak teklif farkı × gerçekleşen tıklama ile bulunur ve
 * o veri koşu anında yok, ertesi gün senkronla geliyor. Faz 6'nın işi.
 */
export const AGENT_FOCUS_WITH_SAVINGS_SIGNAL: readonly AgentFocus[] = [AgentFocus.BUDGET];

/**
 * Kuralın aksiyonlarından odak ÖNERİSİ. Üyelik kaynağı DEĞİL — kullanıcı
 * kuralı hangi ajana bağlayacağına kendi karar verir; bu yalnızca editörde
 * "bu kural Bütçe odağına benziyor" diyen bir yardımcı.
 *
 * Birden çok odak eşleşirse en çok aksiyonu eşleşen kazanır; berabere kalırsa
 * `null` döner (tahmin etmektense sormak).
 */
export function suggestAgentFocus(actions: readonly WorkflowAction[]): AgentFocus | null {
  if (actions.length === 0) return null;

  const scores = AGENT_FOCUSES.map((focus) => ({
    focus,
    score: actions.filter((a) => AGENT_FOCUS_ACTION_TYPES[focus].includes(a.type)).length,
  })).sort((a, b) => b.score - a.score);

  const best = scores[0];
  if (!best || best.score === 0) return null;
  if (scores[1] && scores[1].score === best.score) return null;
  return best.focus;
}

// ═══════════════════════════════════════════════════════════════════════════
// CRUD sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 `__` ÖN EKİ YASAK — tanım `common.ts`te.
 *
 * `AGENT_SYSTEM_WORKFLOW_NAME = '__agent__'` deseni kural listelerinde
 * gizleme filtresi olarak kullanılıyor. Kullanıcının `__` ile başlayan bir ad
 * vermesi, kendi nesnesini kendi ekranından gizlemesi demek. Aynı guard
 * `workflowUpsertSchema.name` üzerinde de var — sabit `common.ts`te duruyor
 * çünkü `automation.ts` de okuyor ve buradan okusaydı iki dosya birbirini
 * import ederdi.
 */
const agentNameSchema = z
  .string()
  .trim()
  .min(2)
  .max(60)
  .refine((n) => !n.startsWith(RESERVED_NAME_PREFIX), { message: 'RESERVED_NAME_PREFIX' });

/**
 * Avatar: emoji ya da uygulama içi bir yol (`/avatars/…`). Serbest URL
 * KABUL EDİLMİYOR — kullanıcının girdiği bir dış URL panelin her ekranına
 * gömülen bir izleme pikseli olabilir ve `Workspace.logoUrl`den farklı olarak
 * burada bir doğrulama akışı yok.
 */
const agentAvatarSchema = z
  .string()
  .trim()
  .max(64)
  .refine((v) => v.length === 0 || v.startsWith('/avatars/') || !/^[a-z]+:/i.test(v), {
    message: 'AVATAR_MUST_BE_EMOJI_OR_LOCAL',
  });

export const agentCreateSchema = z.object({
  name: agentNameSchema,
  avatar: agentAvatarSchema.optional(),
  focus: agentFocusSchema,
  enabled: z.boolean().default(true),
  /// Kurulum anında bağlanacak mevcut kurallar. Boş bırakılabilir — ajan
  /// önce kurulur, kural sonra bağlanır.
  workflowIds: z.array(z.string().uuid()).max(200).optional(),
});
export type AgentCreateInput = z.infer<typeof agentCreateSchema>;

export const agentUpdateSchema = z.object({
  name: agentNameSchema.optional(),
  avatar: agentAvatarSchema.nullable().optional(),
  /// 🔴 `focus` DEĞİŞTİRİLEBİLİR ama kuralları taşımaz. Odak bir etikettir,
  /// bir kapsam değil; değiştirmek altındaki kuralların ne yaptığını
  /// değiştirmez, yalnız uyuşmazlık uyarısı doğurabilir.
  focus: agentFocusSchema.optional(),
  enabled: z.boolean().optional(),
});
export type AgentUpdateInput = z.infer<typeof agentUpdateSchema>;

/// Ajan listesi sekmeleri: aktif / pasif / hepsi.
export const agentListQuerySchema = listQuerySchema.extend({
  status: z.enum(['active', 'paused', 'all']).default('all'),
  focus: agentFocusSchema.optional(),
});
export type AgentListQuery = z.infer<typeof agentListQuerySchema>;

/**
 * KPI aralığı. `metricFilterSchema` KULLANILMIYOR: o platform/hesap/kampanya
 * filtreleri taşıyor ve hiçbiri burada anlamlı değil — ajanın kapsamı
 * altındaki KURALLAR, bir metrik kesiti değil.
 */
export const agentKpiQuerySchema = z.object({
  from: daySchema,
  to: daySchema,
});
export type AgentKpiQuery = z.infer<typeof agentKpiQuerySchema>;

/// Kural ↔ ajan bağını değiştiren tek uç. `agentId: null` = bağı kopar.
export const agentAssignSchema = z.object({
  workflowIds: z.array(z.string().uuid()).min(1).max(200),
});
export type AgentAssignInput = z.infer<typeof agentAssignSchema>;

export interface AgentRow {
  id: string;
  name: string;
  avatar: string | null;
  focus: AgentFocus;
  enabled: boolean;
  /// Altındaki kural sayısı. Ajanın "boş" olup olmadığı ilk bakışta görünmeli:
  /// kuralsız bir ajan hiçbir zaman bir KPI üretmez.
  workflowCount: number;
  /// Altındaki kuralların KAÇI kendi başına açık. `enabled` ajanın kapısı,
  /// bu kuralların kendi kararı; ikisi ayrı sorular.
  enabledWorkflowCount: number;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Tasarruf KPI'ları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Aksiyon başına kazandırılan insan dakikası.
 *
 * 🔴 Bu bir ÖLÇÜM DEĞİL, açıkça ilan edilmiş bir KATSAYI. Faz planı da
 * "aksiyon sayısı × sabit katsayı" diyor. Arayüz bunu "tahmini" diye
 * etiketlemek ZORUNDA; ölçülmüş bir sayı gibi sunmak, yanındaki gerçekten
 * ölçülmüş bütçe rakamının da güvenilirliğini düşürür.
 *
 * 12 dakika: bir kişinin panele girip metriği kontrol edip bütçeyi
 * değiştirmesi ve not alması. Muhafazakâr tarafta tutuldu.
 */
export const AGENT_MINUTES_SAVED_PER_ACTION = 12;

export interface AgentSavingsByCurrency {
  currency: string;
  /// Günlük bütçelerdeki net düşüşün toplamı (pozitif = tasarruf).
  savedPerDay: number;
}

/**
 * 🔴 KPI'nın NE ÖLÇMEDİĞİNİ söyleyen alan.
 *
 * Bir kampanyayı DURAKLATMAK muhtemelen en çok para kazandıran aksiyon ve
 * bugünkü kayıtlardan ÇIKMIYOR: `pause` planında `beforeState: null`
 * (`automation.engine.ts`) ve API/onay yolunda `beforeState` uygulama anında
 * platform sonucuyla eziliyor — hiçbirinde para yok.
 *
 * Karar (kullanıcı, 12 Ağu 2026): boşluk TAHMİN EDİLMİYOR, İLAN EDİLİYOR.
 * Tahmin etmek "yeni veri toplamıyoruz, before/after okuyoruz" kısıtının
 * dışına çıkardı. Ekran "N duraklatma bu toplama girmedi" yazar.
 */
export interface AgentSavingsCoverage {
  /// Toplama GİREMEYEN `pause` aksiyonlarının sayısı.
  pauseActionsExcluded: number;
  /// Neden giremediği — arayüz metnini bu koda bağlar.
  reason: 'PAUSE_HAS_NO_BUDGET_STATE';
}

export interface AgentKpi {
  agentId: string;
  /// Sorgu aralığı (ISO tarih, gün çözünürlüğü).
  from: string;
  to: string;

  /**
   * Günlük bütçe farklarının toplamı.
   *
   * 🔴 Bu bir ORAN, bir TUTAR DEĞİL: "günlük $120 daha az harcanıyor".
   * "Bu ay $3.600 kazandırdı" demek için oranı süreyle çarpmak gerekir ve o
   * çarpım bütçenin o süre boyunca değişmediğini varsayar. Arayüz iki sayıyı
   * ayrı gösterir ve tanımı ekranda YAZAR.
   */
  savedPerDay: number | null;
  /// Karışık para birimi kapsamında `null`; kırılım `byCurrency`de.
  currency: string | null;
  mixedCurrency: boolean;
  byCurrency: AgentSavingsByCurrency[];

  /// Bu odak için para sinyali var mı (`AGENT_FOCUS_WITH_SAVINGS_SIGNAL`).
  /// `false` ise arayüz `0` değil `—` basar.
  hasSavingsSignal: boolean;
  coverage: AgentSavingsCoverage;

  /// Uygulanan aksiyon sayısı × `AGENT_MINUTES_SAVED_PER_ACTION`.
  minutesSaved: number;

  /// Toplam öneri (insan onayına düşmüş aksiyon) ve hâlâ bekleyen.
  proposalsTotal: number;
  proposalsPending: number;
  /**
   * `APPLIED / (APPLIED + reddedilen)`, YALNIZ insan onayına düşmüş satırlar
   * üzerinden.
   *
   * 🔴 Faz planındaki ham `APPLIED / (APPLIED + SKIPPED)` formülü YANLIŞ:
   * AUTOPILOT modunda hiçbir satır onaya düşmez, hepsi `APPLIED` olur ve oran
   * yapay olarak %100 çıkar — insan hiçbir şey onaylamamışken "onay oranı
   * %100" yazardı. Payda `approvedById IS NOT NULL` ile kuruluyor.
   *
   * Onaya hiç düşmemişse `null` (0 değil — "hiç öneri olmadı" ile "hepsi
   * reddedildi" aynı sayı olamaz).
   */
  approvalRate: number | null;
}

/**
 * Kurulum anında gösterilen ÖNGÖRÜ.
 *
 * ⚠️ Yeni ajanın henüz kuralı yoktur, yani kuru çalıştırma yapılamaz.
 * Öngörü, workspace'in SON 30 GÜNDEKİ gerçekleşmiş aksiyonlarından aynı
 * odakta türetiliyor: "senin hesabında bu odak geçen ay şu kadar üretti".
 * Geçmişi olmayan workspace'te `basis: 'NO_HISTORY'` döner ve arayüz sayı
 * BASMAZ — sıfır göstermek yeni kullanıcıyı yanıltırdı.
 */
export interface AgentEstimate {
  focus: AgentFocus;
  basis: 'WORKSPACE_HISTORY_30D' | 'NO_HISTORY' | 'NO_SAVINGS_SIGNAL';
  estimatedSavedPerDay: number | null;
  estimatedMinutesPerMonth: number | null;
  currency: string | null;
  mixedCurrency: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Onay/red ayrımının SABİTLERİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 `ActionStatus`'te `REJECTED` YOK (şema donmuş). Red, `SKIPPED` + `error`
 * alanındaki bir DİZE ile işaretleniyor ve bu dize bugün DÖRT ayrı yerde
 * elle yazılıyor.
 *
 * Onay oranı bu dizelere dayandığı için biri değişirse KPI sessizce yanlış
 * hesaplanır — hata vermez, sadece yanlış bir sayı gösterir. Bu yüzden
 * dizeler buraya taşındı ve `agent-kpi.service.ts` yalnız buradan okuyor.
 */
export const PROPOSAL_REJECTION_ERRORS = [
  /// `approvals.service.ts` — kural önerisini kullanıcı reddetti.
  'REJECTED_BY_USER',
  /// `agent-proposal.service.ts` — sohbet önerisini kullanıcı reddetti.
  /// Not eklenmişse `REJECTED: <not>` biçiminde, o yüzden ÖN EK olarak eşleşir.
  'REJECTED',
] as const;

export const PROPOSAL_EXPIRY_ERRORS = [
  /// `approvals.service.ts` — onay penceresi doldu.
  'APPROVAL_EXPIRED',
  /// `agent-proposal.service.ts` — sohbet önerisi süresi doldu.
  'EXPIRED',
] as const;

/**
 * Süresi dolan öneri onay oranının PAYDASINDA YOK.
 *
 * Kullanıcı reddetmedi — cevap vermedi. İkisini aynı kovaya koymak, tatilde
 * olan bir kullanıcıyı ajanını beğenmemiş gibi gösterirdi. Süre aşımı ayrı
 * bir ürün sorunu (bildirim yetmiyor) ve ayrı ölçülmeli.
 */
export const EXPIRED_PROPOSALS_COUNT_TOWARD_APPROVAL_RATE = false;

/// Aksiyon uygulanabilenler kümesinde mi — KPI sorgusu yalnız bunları sayar.
export const AGENT_KPI_COUNTED_ACTIONS: readonly ActionType[] = ACTION_TYPES_WITH_EXECUTOR;
